import React from 'react';
import {useTranslation} from 'react-i18next';
import classes from '../courses.module.css';
import IntlMessage from '../../../components/IntlMessage/IntlMessage';
import useLocalizedText from '../hooks/useLocalizedText';
import {formatNumber, hoursLabelFull, pluralRu} from '../courses.utils';

/**
 * Тематический план.
 *
 * На десктопе — раскрытая таблица целиком, без аккордеона. Причины конкретные:
 * восемь строк умещаются в один экран, свёрнутый аккордеон печатается пустым
 * (а HR печатает страницу и вкладывает в служебку), и Ctrl+F по нему ничего не находит.
 * Плюс арифметика часов должна читаться взглядом: ИТОГО 16 = два дня отсутствия сотрудника.
 *
 * На узком экране таблица из четырёх колонок нечитаема, поэтому там стек раскрытых
 * карточек — тоже без сворачивания, по тем же причинам.
 */
const SyllabusTable = ({course}) => {
    const pick = useLocalizedText();
    const {t} = useTranslation();
    const modules = course.modules || [];

    const shape = (pick(course.durationShape) || '').trim();
    const totalNote = [
        `${modules.length} ${pluralRu(modules.length, 'модуль', 'модуля', 'модулей')}`,
        shape,
    ].filter(Boolean).join(', ');

    return (
        <>
            <div className={classes.tableWrap}>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th scope="col"><IntlMessage id="coursesPage.course.syllabus.n"/></th>
                            <th scope="col"><IntlMessage id="coursesPage.course.syllabus.topic"/></th>
                            <th scope="col"><IntlMessage id="coursesPage.course.syllabus.content"/></th>
                            <th scope="col" style={{textAlign: 'right'}}>
                                <IntlMessage id="coursesPage.course.syllabus.hours"/>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {modules.map((m) => (
                            <tr key={m.n}>
                                <td className={classes.colNum}>{String(m.n).padStart(2, '0')}</td>
                                <td className={classes.colTopic}>{pick(m.title)}</td>
                                <td className={classes.colContent}>{pick(m.content)}</td>
                                <td className={classes.colHours}>{formatNumber(m.hours)}</td>
                            </tr>
                        ))}
                        <tr className={classes.totalRow}>
                            <td/>
                            <td className={classes.totalLabel}>
                                <IntlMessage id="coursesPage.course.syllabus.total"/>
                            </td>
                            <td className={classes.totalNote}>{totalNote}</td>
                            <td className={classes.totalValue}>{formatNumber(course.durationHours)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={classes.syllabusCards}>
                {modules.map((m) => (
                    <div className={classes.syllabusCard} key={m.n}>
                        <div className={classes.syllabusCardHead}>
                            <span className={classes.syllabusCardNum}>{String(m.n).padStart(2, '0')}</span>
                            <span className={classes.syllabusCardHours}>
                                {formatNumber(m.hours)} {t('coursesPage.course.syllabus.hoursShort')}
                            </span>
                        </div>
                        <h4 className={classes.outcomeTitle}>{pick(m.title)}</h4>
                        <p className={classes.outcomeText}>{pick(m.content)}</p>
                    </div>
                ))}
                <div className={classes.totalPlate}>
                    <span className={classes.totalLabel}>
                        <IntlMessage id="coursesPage.course.syllabus.total"/>
                    </span>
                    <span className={classes.totalValue}>
                        {[hoursLabelFull(course.durationHours), shape].filter(Boolean).join(' · ')}
                    </span>
                </div>
            </div>
        </>
    );
};

export default SyllabusTable;
